import { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { TownNavBar } from '@/components/layout/TownNavBar';
import { AreaNavBar } from '@/components/layout/AreaNavBar';
import { Footer } from '@/components/layout/Footer';
import { RealtorCard } from '@/components/realtor/RealtorCard';
import { PublicThemeProvider } from '@/components/theme/ThemeProvider';
import { format } from 'date-fns';
import { useLoanOfficerAd } from '@/hooks/useLoanOfficerAd';
import { gaEvents } from '@/hooks/useGoogleAnalytics';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  category: string;
  topic: string;
  excerpt: string | null;
  main_image_url: string | null;
  published_at: string;
  tags: string[];
}

interface SiteData {
  id: string;
  name: string;
  location_served: string;
  favicon_url: string | null;
  theme: string;
  header_font: string | null;
  enable_market_value_report?: boolean;
  enable_community_guide?: boolean;
  enable_mortgage_info?: boolean;
  realtor_assigned?: boolean;
  mortgage_ad_image_url?: string | null;
}

interface RealtorData {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  photo_url: string | null;
  logo_url: string | null;
  title_preference: string | null;
  ad_image_url: string | null;
}

interface Town {
  id: string;
  name: string;
  slug: string;
}

interface Area {
  id: string;
  name: string;
  slug: string;
}

const POSTS_PER_PAGE = 12;

export default function BlogIndex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [site, setSite] = useState<SiteData | null>(null);
  const [realtor, setRealtor] = useState<RealtorData | null>(null);
  const [towns, setTowns] = useState<Town[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);

  const currentPage = parseInt(searchParams.get('page') || '1');
  const selectedCategory = searchParams.get('category') || '';
  const selectedTag = searchParams.get('tag') || '';

  // Loan officer ad - uses all site towns
  const townIds = useMemo(() => towns.map(t => t.id), [towns]);
  const { data: loanOfficerAd } = useLoanOfficerAd(site?.id || null, townIds);
  const mortgageAdImageUrl = loanOfficerAd?.ad_image_url || site?.mortgage_ad_image_url;
  const mortgageAdAlt = loanOfficerAd 
    ? `${loanOfficerAd.first_name} ${loanOfficerAd.last_name} - NMLS: ${loanOfficerAd.license_number || 'N/A'} - Mortgage Services`
    : 'Mortgage Information and Tips';

  // Track blog index page view
  useEffect(() => {
    if (site && !loading) {
      gaEvents.viewBlogIndex(currentPage, selectedCategory, selectedTag, posts.length);
    }
  }, [site?.id, currentPage, selectedCategory, selectedTag, loading]);

  useEffect(() => {
    fetchSiteAndPosts();
  }, [currentPage, selectedCategory, selectedTag]);


  const fetchSiteAndPosts = async () => {
    setLoading(true);
    try {
      // Get site by domain
      const hostname = window.location.hostname.replace(/^www\./, '');
      let siteQuery = supabase
        .from('sites_public' as any)
        .select('*')
        .eq('status', 'active');

      if (!hostname.includes('localhost') && !hostname.includes('lovable.app')) {
        siteQuery = siteQuery.or(`primary_domain.eq.${hostname},primary_domain.eq.www.${hostname}`);
      }

      const { data: siteData } = await siteQuery.limit(1).maybeSingle();
      
      if (!siteData) {
        // Fallback: get oldest active site
        const { data: fallbackSite } = await supabase
          .from('sites_public' as any)
          .select('*')
          .eq('status', 'active')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        
        if (fallbackSite) {
          setSite(fallbackSite as unknown as SiteData);
          await fetchPostsForSite((fallbackSite as any).id);
          await fetchRealtorAndTowns((fallbackSite as any).id);
        }
      } else {
        setSite(siteData as unknown as SiteData);
        await fetchPostsForSite((siteData as any).id);
        await fetchRealtorAndTowns((siteData as any).id);
      }
    } catch (error) {
      console.error('Error fetching blog data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPostsForSite = async (siteId: string) => {
    // First, fetch all unique categories that have published posts
    const { data: categoryData } = await supabase
      .from('blog_posts')
      .select('category')
      .eq('site_id', siteId)
      .eq('is_published', true)
      .lte('published_at', new Date().toISOString());

    if (categoryData) {
      const uniqueCategories = [...new Set(categoryData.map(p => p.category))].sort();
      setAvailableCategories(uniqueCategories);
    }

    // Build query for posts
    let query = supabase
      .from('blog_posts')
      .select('id, slug, title, category, topic, excerpt, main_image_url, published_at', { count: 'exact' })
      .eq('site_id', siteId)
      .eq('is_published', true)
      .lte('published_at', new Date().toISOString())
      .order('published_at', { ascending: false });

    if (selectedCategory) {
      query = query.eq('category', selectedCategory);
    }

    // Pagination
    const from = (currentPage - 1) * POSTS_PER_PAGE;
    const to = from + POSTS_PER_PAGE - 1;
    query = query.range(from, to);

    const { data: postsData, count } = await query;

    if (postsData) {
      // Fetch tags for each post
      const postIds = postsData.map(p => p.id);
      const { data: tagsData } = await supabase
        .from('blog_post_tags')
        .select('blog_post_id, tag_id, blog_tags(name)')
        .in('blog_post_id', postIds);

      const postTagsMap: Record<string, string[]> = {};
      tagsData?.forEach((t: any) => {
        if (!postTagsMap[t.blog_post_id]) {
          postTagsMap[t.blog_post_id] = [];
        }
        if (t.blog_tags?.name) {
          postTagsMap[t.blog_post_id].push(t.blog_tags.name);
        }
      });

      // Filter by tag if selected
      let filteredPosts = postsData.map(p => ({
        ...p,
        tags: postTagsMap[p.id] || []
      }));

      if (selectedTag) {
        filteredPosts = filteredPosts.filter(p => p.tags.includes(selectedTag));
      }

      setPosts(filteredPosts);
      setTotalCount(count || 0);

      // Collect available tags
      const allTags = new Set<string>();
      Object.values(postTagsMap).forEach(tags => {
        tags.forEach(tag => allTags.add(tag));
      });
      setAvailableTags(Array.from(allTags).sort());
    }
  };

  const fetchRealtorAndTowns = async (siteId: string) => {
    const [realtorRes, townsRes] = await Promise.all([
      supabase.from('realtor_profiles').select('*').eq('site_id', siteId).single(),
      supabase.from('towns').select('id, name, slug').eq('site_id', siteId).eq('is_published', true).order('name')
    ]);

    if (realtorRes.data) setRealtor(realtorRes.data as RealtorData);
    if (townsRes.data) {
      setTowns(townsRes.data);
      
      // If only one town, fetch its areas for the AreaNavBar
      if (townsRes.data.length === 1) {
        const { data: areasData } = await supabase
          .from('areas')
          .select('id, name, slug')
          .eq('town_id', townsRes.data[0].id)
          .eq('is_published', true)
          .order('name');
        
        if (areasData) setAreas(areasData);
      }
    }
  };

  const handleCategoryChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set('category', value);
    } else {
      params.delete('category');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handleTagClick = (tag: string) => {
    const params = new URLSearchParams(searchParams);
    if (selectedTag === tag) {
      params.delete('tag');
    } else {
      params.set('tag', tag);
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(totalCount / POSTS_PER_PAGE);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading...</title>
          <meta name="description" content="" />
          <meta property="og:title" content="" />
          <meta property="og:description" content="" />
          <meta name="twitter:title" content="" />
          <meta name="twitter:description" content="" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-muted">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (!site) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <p className="text-muted-foreground">Site not found</p>
      </div>
    );
  }

  const showRealtor = site.realtor_assigned !== false && realtor;
  const pageTitle = currentPage > 1 ? `Blog - Page ${currentPage} | ${site.name}` : `Blog | ${site.name}`;
  const blogDescription = `Read the latest real estate insights, local guides, and market updates from ${site.name}.`;
  const canonicalUrl = `${window.location.origin}/blog${currentPage > 1 ? `?page=${currentPage}` : ''}`;
  const domainName = window.location.origin.replace(/^https?:\/\//, '').replace(/\/$/, '');

  // ItemList schema for blog post collection (helps search engines understand list structure)
  const itemListSchema = posts.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `${site.name} Blog Articles`,
    "description": blogDescription,
    "numberOfItems": posts.length,
    "itemListElement": posts.map((post, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "url": `${window.location.origin}/blog/${post.slug}`,
      "name": post.title
    }))
  } : null;

  // WebSite schema for site name recognition
  const webSiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${window.location.origin}/#website`,
    "name": site.name,
    "alternateName": domainName || undefined,
    "url": window.location.origin
  };

  return (
    <PublicThemeProvider themeId={site.theme} headerFontId={site.header_font || 'lora'}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={blogDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={site.name} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={blogDescription} />
        <meta property="og:url" content={canonicalUrl} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={blogDescription} />

        {/* Structured Data - WebSite */}
        <script type="application/ld+json">
          {JSON.stringify(webSiteSchema)}
        </script>

        {/* Structured Data - ItemList */}
        {itemListSchema && (
          <script type="application/ld+json">
            {JSON.stringify(itemListSchema)}
          </script>
        )}
      </Helmet>

      <Header 
        siteName={site.name} 
        logoUrl={showRealtor ? realtor?.logo_url : undefined}
        faviconUrl={site.favicon_url}
        enableMarketValueReport={site.enable_market_value_report !== false}
        enableCommunityGuide={site.enable_community_guide !== false}
        enableMortgageInfo={site.enable_mortgage_info !== false}
      />

      <TownNavBar 
        towns={towns} 
        isPreview={false}
        enableMarketValueReport={site.enable_market_value_report !== false}
        enableMortgageInfo={site.enable_mortgage_info !== false}
        realtorFirstName={realtor?.first_name}
        themeId={site.theme}
      />
      
      {/* Area Navigation Bar - Show when only one town exists */}
      {towns.length === 1 && areas.length > 0 && (
        <AreaNavBar areas={areas} townSlug={towns[0].slug} />
      )}

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-primary/5 to-background py-12 md:py-16">
          <div className="container px-4 sm:px-6 lg:px-8">
            <nav className="mb-6" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li>/</li>
                <li className="text-foreground font-medium">Blog</li>
              </ol>
            </nav>
            <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              Real Estate Blog
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Local insights, market updates, and expert advice for {site.location_served}.
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="border-b border-border">
          <div className="container px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filter:</span>
              </div>
              <Select value={selectedCategory || 'all'} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {availableCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedTag && (
                <Badge 
                  variant="secondary" 
                  className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                  onClick={() => handleTagClick(selectedTag)}
                >
                  {selectedTag} ×
                </Badge>
              )}
            </div>
          </div>
        </section>

        {/* Content Grid */}
        <section className="py-12">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3">
                {posts.length === 0 ? (
                  <Card className="p-12 text-center">
                    <p className="text-muted-foreground">No blog posts found.</p>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {posts.map((post) => (
                      <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                        <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow">
                          {post.main_image_url && (
                            <div className="aspect-video overflow-hidden">
                              <img 
                                src={post.main_image_url} 
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          )}
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                              <Calendar className="h-3 w-3" />
                              <time dateTime={post.published_at}>
                                {format(new Date(post.published_at), 'MMM d, yyyy')}
                              </time>
                              <span>•</span>
                              <span>{post.category}</span>
                            </div>
                            <h2 className="font-display font-semibold text-lg mb-2 text-card-foreground group-hover:text-accent transition-colors line-clamp-2">
                              {post.title}
                            </h2>
                            {post.excerpt && (
                              <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                                {post.excerpt}
                              </p>
                            )}
                            <div className="flex flex-wrap gap-1">
                              {post.tags.slice(0, 3).map(tag => (
                                <Badge 
                                  key={tag} 
                                  variant="outline" 
                                  className="text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleTagClick(tag);
                                  }}
                                >
                                  {tag}
                                </Badge>
                              ))}
                              {post.tags.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{post.tags.length - 3}
                                </Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground px-4">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {showRealtor && <RealtorCard realtor={realtor} />}

                {/* Popular Tags */}
                {availableTags.length > 0 && (
                  <Card className="p-4">
                    <h3 className="font-display font-semibold mb-3">Popular Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {availableTags.slice(0, 15).map(tag => (
                        <Badge 
                          key={tag}
                          variant={selectedTag === tag ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => handleTagClick(tag)}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Mortgage Info Ad */}
                {site.enable_mortgage_info !== false && mortgageAdImageUrl && (
                  <Link to="/mortgages" className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow block">
                    <img 
                      src={mortgageAdImageUrl} 
                      alt={mortgageAdAlt} 
                      className="w-full h-auto object-cover"
                    />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer 
        siteName={site.name} 
        locationServed={site.location_served} 
        towns={towns}
        areas={areas}
        realtor={showRealtor ? realtor : null}
        enableBuyers={true}
        enableSellers={true}
        enableMortgageInfo={site.enable_mortgage_info !== false}
      />
    </PublicThemeProvider>
  );
}